/**
 * Cached Pixi textures for the game. Low-poly buildings and upright unit views
 * are authored in lowpoly.ts; this bank owns their resolution and lifetime.
 * Terrain, effects, tactical tokens and separately animated weapons live here.
 * Unit geometry is baked by direction on first use, while faction architecture
 * is cached per vernacular. No rendering code changes the simulation.
 */

import type { Texture} from 'pixi.js';
import { Container, Graphics, Matrix, RenderTexture, type Renderer } from 'pixi.js';
import { C, mix, shade } from '../data/palette.ts';
import { UNIT_TYPES, type UnitType, type VisWeapon } from '../data/units.ts';
import { BUILDING_TYPES, type BuildingType } from '../data/buildings.ts';
import { faction, type ArchitectureStyle } from '../data/factions.ts';
import {
  ASHEN_BASELINE,
  vernacularForStyle,
  vernacularOf,
  type Vernacular,
} from './vernacular.ts';
import { Terrain, UF } from '../sim/types.ts';
import { hash2 } from '../core/math.ts';
import {
  MARK_MAX_LUMA,
  TERRAIN_IDS,
  capLuma,
  surfaceTable,
  themeFor,
  type BiomeTheme,
  type Surface,
} from './biome.ts';
import { drawLowPolyBuilding, drawLowPolyUnit, lowPolyHand } from './lowpoly.ts';
import { UNIT_WALK_FRAMES, UNIT_WORK_FRAMES, type UnitPose } from './unitmotion.ts';
import { COMPANY_ICONS, drawCompanyIcon, type CompanyIcon } from './companyicons.ts';
import { loadBlenderBuildingAssets, type BlenderAssetLoadOptions, type BlenderBuildingArt, type BlenderBuildingId } from './blenderassets.ts';

/** Full authored sets are large; keep the current architecture and two recent sets. */
export const BUILDING_STYLE_CACHE_LIMIT = 3;

/**
 * Sparse surface highlights retain their material colour. The mark limit is
 * close to white, so snow crests are pale rather than artificially dark gray.
 */
function mark(c: number): number {
  return capLuma(c, MARK_MAX_LUMA);
}

/** A shadow mark's colour: dark by construction, so it needs no ceiling. */
function ink(c: number): number {
  return shade(c, 0.7);
}

/**
 * Pixels per tile that unit art is authored at. Sprites are scaled from here,
 * so this is a *resolution* knob and not a size knob: `scene` divides by it to
 * get the draw scale, and raising it buys drawing room without changing how big
 * anything appears. It was raised from 56 when brutes and champions started
 * growing capes and horns that the old box sawed off.
 */
export const ART_UNIT_PX = 72;
export const ART_TILE_PX = 32;

export interface UnitArt {
  /** Frame 0. Kept as its own field so nothing that only wants a still breaks. */
  body: Texture;
  /** Six articulated poses; rolling engines share their one still texture. */
  frames: Texture[];
  /** Upright 3D miniature views, +x first, in eighth-turn increments. Baked lazily. */
  readonly directionFrames?: Texture[][];
  readonly directionIdle?: Texture[];
  readonly directionWorkFrames?: Texture[][];
  /** Projected hand positions in authoring pixels, relative to the ground anchor. */
  readonly directionHands?: Array<{ x: number; y: number }>;
  readonly directionFrameHands?: Array<Array<{ x: number; y: number }>>;
  readonly directionWorkHands?: Array<Array<{ x: number; y: number }>>;
  /** Asymmetric headroom keeps mounted units and standards inside the texture. */
  readonly bodyAnchorY?: number;
  weapon: Texture;
  /** Pivot offset for the weapon, as a fraction of 48 — see `bakeUnit`. */
  weaponPivotX: number;
  weaponPivotY: number;
  /** Extra scale to apply when drawing. */
  scale: number;
}

/**
 * Pick a walk frame. `step` is any integer that advances as the unit covers
 * ground — distance travelled in half-tiles is the cheapest honest source, and
 * it means a routing man's legs go faster than a marching one's for free.
 */
export function walkFrame(art: UnitArt, step: number): Texture {
  return art.frames[((step % art.frames.length) + art.frames.length) % art.frames.length];
}

export interface BuildingGeometry {
  silhouette: Array<[number, number]>;
  groundCenterX: number;
  groundFootprint: Array<[number, number]>;
  groundFrontY: number;
  bounds: { x0: number; y0: number; x1: number; y1: number };
  /** Optional projected stack opening, in logical texture pixels. */
  chimney?: [number, number];
}

export class TextureBank {
  readonly renderer: Renderer;

  /** [typeIndex][team] → art. Team 0 = warm rim, 1 = cold rim. */
  readonly units: UnitArt[][] = [];
  /**
   * Buildings for the architecture currently in play, indexed by type.
   *
   * Mutated in place by `setArchitecture` rather than replaced, because the
   * scene reads straight through `bank.buildings[typeId]` and a fresh array
   * would leave it holding the old one.
   */
  readonly buildings: Texture[] = [];
  /** Projected ground depth and pick shape, in the baked texture's pixels. */
  readonly buildingGeometry: BuildingGeometry[] = [];
  /** Cached world-tile margin for artwork extending beyond its logical lot. */
  buildingCullPadding = 6;
  readonly terrain: Texture[][] = [];
  readonly walls: Record<string, Texture> = {};

  /**
   * War-map tokens, used once the camera pulls back past the point where a
   * silhouette is legible. Index by `tokenIndexFor`.
   */
  readonly tokens: Texture[] = [];
  /** Company role badges, baked once and shared by every banner. */
  readonly companyIcons = new Map<CompanyIcon, Texture>();

  blob!: Texture;
  spark!: Texture;
  smoke!: Texture;
  ember!: Texture;
  blood!: Texture;
  ring!: Texture;
  arrow!: Texture;
  bolt!: Texture;
  stone!: Texture;
  magic!: Texture;
  siegeShot!: Texture;
  crater!: Texture;
  corpse!: Texture;
  scorch!: Texture;
  /** A company standard: white, tinted to the faction accent at draw time. */
  banner!: Texture;
  reticle!: Texture;
  glow!: Texture;
  white!: Texture;

  /** Baked building sets, one per vernacular, cut only when first asked for. */
  private readonly buildingsByStyle = new Map<string, Array<{ texture: Texture; geometry: BuildingGeometry }>>();
  private readonly blenderBuildingsByStyle = new Map<string, Map<BlenderBuildingId, BlenderBuildingArt>>();
  private readonly buildingAssetLoads = new Map<string, Promise<void>>();
  private readonly buildingAssetErrorsByStyle = new Map<string, string[]>();
  private readonly pendingBuildingStyles = new Set<string>();
  private readonly buildingStyleUsedAt = new Map<string, number>();
  private buildingStyleClock = 0;
  private autoLoadBuildingStyles = false;
  private vernacular: Vernacular = ASHEN_BASELINE;

  /**
   * The country the ground is painted for. See `render/biome.ts` and `setBiome`.
   *
   * Held beside `vernacular` and moved the same way: the two are independent —
   * a Karrun hold can stand in a marsh — so they are two fields and two bakes.
   */
  private theme: BiomeTheme = themeFor(undefined);

  constructor(renderer: Renderer) {
    this.renderer = renderer;
  }

  /** Render a display object into a texture. Public so the scene can bake terrain chunks. */
  bake(g: Container, w: number, h: number, resolution = 1): Texture {
    const rt = RenderTexture.create({ width: w, height: h, resolution, antialias: true });
    this.renderer.render({ container: g, target: rt, clear: true });
    return rt;
  }

  /** Bake with the drawing origin at the texture centre. */
  private bakeCentred(draw: (g: Graphics) => void, size: number): Texture {
    const g = new Graphics();
    draw(g);
    const holder = new Container();
    holder.addChild(g);
    holder.setFromMatrix(new Matrix(1, 0, 0, 1, size / 2, size / 2));
    return this.bake(holder, size, size);
  }

  build(): void {
    this.buildPrimitives();
    this.buildTokens();
    for (const icon of COMPANY_ICONS) {
      this.companyIcons.set(icon, this.bakeCentred((g) => drawCompanyIcon(g, icon), 32));
    }
    this.buildTerrain();
    this.buildWalls();
    this.buildUnits();
    this.setVernacular('ashen');
  }

  /**
   * Point the building bank at a people's vernacular.
   *
   * Vernaculars are baked on demand. A campaign can visit many peoples, so
   * completed inactive sets leave a three-style cache in least-recent-use
   * order. The current scene and every in-flight load stay protected.
   *
   * **Nothing called this before.** `build()` set `'stone'` and no other line in
   * the codebase ever moved it, so every settlement in the game — Karrun-Dûr,
   * the Moonbound, the Pale Litany — was drawn in Ashen grey rubble, and the
   * whole architecture table was dead code the day it was written. Wiring it up
   * was worth more than anything added to it since. `Scene.render` now watches
   * `world.raceId` and calls this when it moves.
   */
  setVernacular(id: string): void {
    this.applySet(vernacularOf(id));
  }

  /** The same, for anything that only knows a faction. See `vernacularForStyle`. */
  setArchitecture(style: ArchitectureStyle): void {
    this.applySet(vernacularForStyle(style));
  }

  private applySet(vern: Vernacular): void {
    this.buildingStyleUsedAt.set(vern.id, ++this.buildingStyleClock);
    const imported = this.blenderBuildingsByStyle.get(vern.id);
    let set = this.buildingsByStyle.get(vern.id);
    if (!set) {
      set = BUILDING_TYPES.map((def) => imported?.get(def.id) || this.bakeBuilding(def, vern));
      this.buildingsByStyle.set(vern.id, set);
    }
    this.vernacular = vern;
    this.buildings.length = 0;
    this.buildingGeometry.length = 0;
    let extent = 0;
    for (const art of set) {
      this.buildings.push(art.texture);
      this.buildingGeometry.push(art.geometry);
      const { bounds, groundCenterX, groundFrontY } = art.geometry;
      extent = Math.max(extent, groundCenterX - bounds.x0, bounds.x1 - groundCenterX,
        groundFrontY - bounds.y0, bounds.y1 - groundFrontY);
    }
    this.buildingCullPadding = Math.max(6, Math.ceil(extent / ART_TILE_PX) + 1);
    if (this.autoLoadBuildingStyles) void this.loadBuildingAssets({ style: vern.id });
    this.trimBuildingStyles();
  }

  /** Load other faction packs only when their settlement is first visited. */
  enableBuildingAssetStyles(): void { this.autoLoadBuildingStyles = true; }

  /** Optional authored art, loaded once per style. A late load replaces its
   * cached entries and releases those superseded generated GPU textures. */
  loadBuildingAssets(options?: BlenderAssetLoadOptions): Promise<void> {
    const style = options?.style ?? 'ashen';
    this.buildingStyleUsedAt.set(style, ++this.buildingStyleClock);
    const existing = this.buildingAssetLoads.get(style);
    if (existing) return existing;
    this.pendingBuildingStyles.add(style);
    const pending = loadBlenderBuildingAssets(options).then(result => {
      this.buildingAssetErrorsByStyle.set(style, result.errors);
      const imported = new Map<BlenderBuildingId, BlenderBuildingArt>();
      this.blenderBuildingsByStyle.set(style, imported);
      const cached = this.buildingsByStyle.get(style);
      for (const [id, art] of result.assets) {
        imported.set(id, art);
        if (cached) {
          const index = BUILDING_TYPES.findIndex(def => def.id === id);
          const old = cached[index];
          cached[index] = art;
          old.texture.destroy(true);
        }
      }
      if (cached && this.vernacular.id === style) this.applySet(this.vernacular);
    }).finally(() => {
      this.pendingBuildingStyles.delete(style);
      this.trimBuildingStyles();
    });
    this.buildingAssetLoads.set(style, pending);
    return pending;
  }

  /** Evict only inactive completed sets. The bank's live texture/geometry arrays
   * remain untouched; pending sets may temporarily exceed the normal limit. */
  private trimBuildingStyles(): void {
    const resident = new Set([...this.buildingsByStyle.keys(), ...this.blenderBuildingsByStyle.keys()]);
    if (resident.size <= BUILDING_STYLE_CACHE_LIMIT) return;
    const candidates = [...resident].filter(style => style !== this.vernacular.id && !this.pendingBuildingStyles.has(style))
      .sort((a, b) => (this.buildingStyleUsedAt.get(a) ?? 0) - (this.buildingStyleUsedAt.get(b) ?? 0));
    for (const style of candidates) {
      if (resident.size <= BUILDING_STYLE_CACHE_LIMIT) break;
      // Imported textures occur in both indexes once the set has been shown.
      // Deduplicate their ownership so each GPU source is destroyed once.
      const textures = new Set([
        ...(this.buildingsByStyle.get(style)?.map(art => art.texture) ?? []),
        ...[...(this.blenderBuildingsByStyle.get(style)?.values() ?? [])].map(art => art.texture),
      ]);
      for (const texture of textures) if (!texture.destroyed) texture.destroy(true);
      this.buildingsByStyle.delete(style);
      this.blenderBuildingsByStyle.delete(style);
      this.buildingAssetLoads.delete(style);
      this.buildingAssetErrorsByStyle.delete(style);
      this.buildingStyleUsedAt.delete(style);
      resident.delete(style);
    }
  }

  /** Debug/QA accessors describe the live bank without changing its selection. */
  get loadedBuildingAssetIds(): readonly string[] { return [...(this.blenderBuildingsByStyle.get(this.vernacular.id)?.keys() ?? [])].sort(); }
  get buildingAssetLoadErrors(): readonly string[] { return this.buildingAssetErrorsByStyle.get(this.vernacular.id) ?? []; }
  buildingArtSource(typeIndex: number): 'blender' | 'generated' {
    const id = BUILDING_TYPES[typeIndex]?.id as BlenderBuildingId | undefined;
    return id !== undefined && this.blenderBuildingsByStyle.get(this.vernacular.id)?.has(id) ? 'blender' : 'generated';
  }

  /** Which people the building bank is currently showing. */
  get vernacularId(): string {
    return this.vernacular.id;
  }

  /**
   * Token shapes, drawn in white and tinted at draw time. Shape carries role,
   * tint carries side — so a pulled-back view reads like a war table: you can
   * see that the wedge of diamonds is cavalry and that it is coming for your
   * left, without being able to see a single soldier.
   */
  private buildTokens(): void {
    const S = 40;
    const R = 13;
    const shapes: Array<(g: Graphics) => void> = [
      // 0 infantry — a solid square, the block of the line
      (g) => g.rect(-R * 0.8, -R * 0.8, R * 1.6, R * 1.6).fill(0xffffff),
      // 1 archer — an inverted triangle, arrow-like
      (g) => g.poly([-R, -R * 0.8, R, -R * 0.8, 0, R]).fill(0xffffff),
      // 2 cavalry — a diamond pointing the way it faces
      (g) => g.poly([R * 1.15, 0, 0, -R * 0.85, -R * 0.8, 0, 0, R * 0.85]).fill(0xffffff),
      // 3 siege — a heavy hexagon
      (g) => {
        const pts: number[] = [];
        for (let i = 0; i < 6; i++) {
          const a = (i / 6) * Math.PI * 2;
          pts.push(Math.cos(a) * R, Math.sin(a) * R);
        }
        g.poly(pts).fill(0xffffff);
      },
      // 4 caster — a circle with a hollow centre
      (g) => {
        g.circle(0, 0, R * 0.95).fill(0xffffff);
        g.circle(0, 0, R * 0.4).fill({ color: 0x000000, alpha: 1 });
      },
      // 5 large/monstrous — a fat rounded blob
      (g) => g.circle(0, 0, R * 1.15).fill(0xffffff),
      // 6 villager — a small dot
      (g) => g.circle(0, 0, R * 0.45).fill(0xffffff),
      // 7 champion — a star
      (g) => {
        const pts: number[] = [];
        for (let i = 0; i < 10; i++) {
          const a = (i / 10) * Math.PI * 2 - Math.PI / 2;
          const rr = i % 2 === 0 ? R * 1.25 : R * 0.52;
          pts.push(Math.cos(a) * rr, Math.sin(a) * rr);
        }
        g.poly(pts).fill(0xffffff);
      },
      // 8 swarm — a cluster, because the point of a swarm is that it is many
      (g) => {
        g.circle(-R * 0.42, -R * 0.36, R * 0.42).fill(0xffffff);
        g.circle(R * 0.46, -R * 0.2, R * 0.38).fill(0xffffff);
        g.circle(-R * 0.06, R * 0.5, R * 0.44).fill(0xffffff);
      },
      // 9 flier — a pair of swept wings, which reads the same at any rotation
      (g) => {
        g.poly([0, -R * 0.3, -R * 1.3, -R * 0.95, -R * 0.5, R * 0.25, 0, R * 0.5]).fill(0xffffff);
        g.poly([0, -R * 0.3, R * 1.3, -R * 0.95, R * 0.5, R * 0.25, 0, R * 0.5]).fill(0xffffff);
      },
    ];
    for (const draw of shapes) {
      this.tokens.push(this.bakeCentred(draw, S));
    }
  }

  // ── Primitives ───────────────────────────────────────────────────────────

  private buildPrimitives(): void {
    this.white = this.bakeCentred((g) => {
      g.rect(-4, -4, 8, 8).fill(0xffffff);
    }, 8);

    // Soft radial blob — shadows, glows, smoke cores.
    this.blob = this.bakeCentred((g) => {
      for (let i = 12; i >= 1; i--) {
        g.circle(0, 0, (i / 12) * 30).fill({ color: 0xffffff, alpha: 0.055 });
      }
    }, 64);

    this.glow = this.bakeCentred((g) => {
      for (let i = 16; i >= 1; i--) {
        const t = i / 16;
        g.circle(0, 0, t * 46).fill({ color: 0xffffff, alpha: 0.035 * (1 - t) + 0.01 });
      }
    }, 96);

    this.spark = this.bakeCentred((g) => {
      g.moveTo(-9, 0).lineTo(9, 0).stroke({ width: 2.4, color: 0xffffff, alpha: 1, cap: 'round' });
      g.circle(0, 0, 2.1).fill({ color: 0xffffff });
    }, 24);

    this.smoke = this.bakeCentred((g) => {
      for (let i = 10; i >= 1; i--) {
        g.circle(0, 0, (i / 10) * 26).fill({ color: 0xffffff, alpha: 0.05 });
      }
      g.circle(-6, -4, 9).fill({ color: 0xffffff, alpha: 0.06 });
      g.circle(7, 3, 7).fill({ color: 0xffffff, alpha: 0.05 });
    }, 56);

    this.ember = this.bakeCentred((g) => {
      g.circle(0, 0, 3.2).fill({ color: 0xffffff });
      g.circle(0, 0, 6.5).fill({ color: 0xffffff, alpha: 0.28 });
    }, 20);

    this.blood = this.bakeCentred((g) => {
      g.ellipse(0, 0, 6, 4).fill({ color: 0xffffff });
      g.circle(6, 2, 2.2).fill({ color: 0xffffff, alpha: 0.9 });
      g.circle(-5, -3, 1.7).fill({ color: 0xffffff, alpha: 0.8 });
    }, 24);

    this.ring = this.bakeCentred((g) => {
      g.circle(0, 0, 28).stroke({ width: 3, color: 0xffffff, alpha: 1 });
      g.circle(0, 0, 22).stroke({ width: 1, color: 0xffffff, alpha: 0.45 });
    }, 64);

    this.reticle = this.bakeCentred((g) => {
      g.circle(0, 0, 60).stroke({ width: 2, color: 0xffffff, alpha: 0.9 });
      g.circle(0, 0, 52).stroke({ width: 1, color: 0xffffff, alpha: 0.3 });
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2;
        g.moveTo(Math.cos(a) * 44, Math.sin(a) * 44)
          .lineTo(Math.cos(a) * 58, Math.sin(a) * 58)
          .stroke({ width: 2, color: 0xffffff, alpha: 0.75 });
      }
    }, 128);

    // Projectiles.
    this.arrow = this.bakeCentred((g) => {
      g.moveTo(-11, 0).lineTo(9, 0).stroke({ width: 1.6, color: C.bone, cap: 'round' });
      g.poly([9, 0, 4, -2.4, 4, 2.4]).fill(C.boneDim);
      g.poly([-11, 0, -7, -3, -7, 3]).fill({ color: C.boneDeep, alpha: 0.9 });
    }, 28);

    this.bolt = this.bakeCentred((g) => {
      g.moveTo(-8, 0).lineTo(8, 0).stroke({ width: 2.6, color: 0x9c968a, cap: 'round' });
      g.poly([9, 0, 3, -3, 3, 3]).fill(0xcfc7b6);
    }, 24);

    this.stone = this.bakeCentred((g) => {
      g.poly([-5, -3, 2, -6, 6, 0, 3, 5, -4, 4]).fill(0x6a655c);
      g.poly([-5, -3, 2, -6, 0, -1]).fill(0x847e73);
    }, 20);

    this.magic = this.bakeCentred((g) => {
      g.circle(0, 0, 5).fill({ color: 0xffffff });
      g.circle(0, 0, 10).fill({ color: 0xffffff, alpha: 0.35 });
      g.circle(0, 0, 15).fill({ color: 0xffffff, alpha: 0.12 });
    }, 40);

    this.siegeShot = this.bakeCentred((g) => {
      g.circle(0, 0, 8).fill(0x4a453e);
      g.circle(-2.5, -2.5, 3.4).fill({ color: 0x6d675e, alpha: 0.8 });
    }, 28);

    // Ground decals.
    this.crater = this.bakeCentred((g) => {
      g.ellipse(0, 0, 34, 26).fill({ color: 0x100e0c, alpha: 0.5 });
      g.ellipse(0, 0, 26, 19).fill({ color: 0x1c1815, alpha: 0.45 });
      g.ellipse(-4, -3, 14, 10).fill({ color: 0x2a2420, alpha: 0.35 });
    }, 80);

    this.scorch = this.bakeCentred((g) => {
      for (let i = 0; i < 9; i++) {
        const a = (i / 9) * Math.PI * 2;
        const r = 16 + (i % 3) * 6;
        g.circle(Math.cos(a) * r * 0.55, Math.sin(a) * r * 0.5, r * 0.5).fill({
          color: 0x0d0b09,
          alpha: 0.24,
        });
      }
    }, 80);

    this.corpse = this.bakeCentred((g) => {
      g.ellipse(0, 0, 11, 6).fill({ color: 0x1a1613, alpha: 0.85 });
      g.ellipse(-6, -1, 4, 3.4).fill({ color: 0x241f1a, alpha: 0.9 });
      g.ellipse(6, 1, 3, 2.6).fill({ color: 0x241f1a, alpha: 0.75 });
    }, 40);

    /**
     * The company standard.
     *
     * Tall enough to clear a rank of men and outlined like a unit, because that
     * is what it is for: at tactical zoom the pole is how you find the leader,
     * and a pennant that does not break the skyline finds nobody. Drawn white
     * and tinted to the faction accent by whoever plants it.
     */
    this.banner = this.bakeCentred((g) => {
      g.moveTo(0, 26).lineTo(0, -30).stroke({ width: 5.4, color: 0x0d0b09, alpha: 0.9, cap: 'round' });
      g.moveTo(0, 26).lineTo(0, -30).stroke({ width: 2.6, color: 0xffffff, cap: 'round' });
      const flag = [0, -30, 22, -25, 17, -17, 22, -9, 0, -13];
      g.poly(flag).fill({ color: 0x0d0b09, alpha: 0.9 }).stroke({ width: 4, color: 0x0d0b09, alpha: 0.9, join: 'round' });
      g.poly(flag).fill(0xffffff);
      g.circle(0, -31, 3.2).fill({ color: 0x0d0b09, alpha: 0.9 });
      g.circle(0, -31, 2.0).fill(0xffffff);
    }, 72);
  }

  // ── Terrain ──────────────────────────────────────────────────────────────

  /**
   * The terrain layer's *marks* — and only its marks.
   *
   * These used to be the whole of the ground: an opaque 32-pixel tile per
   * terrain, four variants deep, butted edge to edge. That is why the
   * settlement's floor read as a tiled floor, and why grass met forest on a
   * straight line down a tile boundary.
   *
   * The colour, the shape and the coarse grain of the ground now come from
   * `render/ground.ts`, baked per chunk at six pixels to a tile and stretched
   * under this layer. **What is left here is transparent**: tufts, pebbles,
   * reeds, cracks, planks and ripples at the pitch a player sees when they wind
   * the camera all the way in, over ground that already has a colour and a
   * light. Two layers, two pitches — the split `ui/relief.ts` argued for, and
   * the reason neither of them has to be a compromise.
   *
   * Re-baked when the country changes, which is once per world.
   */
  private buildTerrain(): void {
    const surf = surfaceTable(this.theme);
    for (const t of TERRAIN_IDS) {
      const old = this.terrain[t];
      const variants: Texture[] = [];
      for (let v = 0; v < 4; v++) {
        variants.push(this.bakeTerrainTile(t, surf[t], v));
      }
      this.terrain[t] = variants;
      if (old) for (const tex of old) tex.destroy(true);
    }
  }

  /**
   * Point the ground at a country. See `render/biome.ts`.
   *
   * A string compare a frame in `Scene.render`, and the bake behind it happens
   * once per world — the same shape as `setVernacular`, and for the same
   * reason: there are three doors into a settlement now and a renderer that
   * reads the world it is given cannot be out of step with it.
   */
  setBiome(biome: string | undefined): void {
    const next = themeFor(biome);
    if (next.id === this.theme.id) return;
    this.theme = next;
    this.buildTerrain();
  }

  /** Which country the ground is currently painted for. */
  get biomeId(): string {
    return this.theme.id;
  }

  private bakeTerrainTile(terrain: number, pal: Surface, variant: number): Texture {
    const S = ART_TILE_PX;
    const g = new Graphics();

    /**
     * Grain: a scatter of flecks, seeded per variant — and **no base fill**.
     *
     * The one line that used to be here, `g.rect(0, 0, S, S).fill(pal.base)`,
     * is the whole of what made this a tiled floor. Everything below now
     * composites over the chunk's own ground.
     */
    for (let i = 0; i < 26; i++) {
      const x = hash2(i, variant, 17) * S;
      const y = hash2(i, variant, 91) * S;
      const r = 0.7 + hash2(i, variant, 55) * 2.1;
      const light = hash2(i, variant, 7) > 0.5;
      g.circle(x, y, r).fill({ color: light ? pal.alt : pal.ink, alpha: 0.33 });
    }

    switch (terrain) {
      case Terrain.Forest: {
        /**
         * The floor under the wood — **not** trees.
         *
         * This used to draw three dead trunks per tile, which was right when
         * nothing else did: forest was a terrain and there was no such thing as
         * a tree. There is now. `sim/systems/nodes.ts` stands real trees on this
         * ground, `render/nodeart.ts` gives each one a silhouette, and a
         * dead trunk baked into every tile underneath them was a second, worse
         * forest drawn through the first.
         *
         * What a forest floor has that open ground does not is root, deadfall
         * and low brush, so that is what is here: faceted scrub lit from the
         * north-west like everything else, and roots running out from under it.
         */
        for (let i = 0; i < 3; i++) {
          const x = 6 + hash2(i, variant, 3) * (S - 12);
          const y = 8 + hash2(i, variant, 29) * (S - 14);
          const r = 2.6 + hash2(i, variant, 61) * 2.6;
          // A bush as three facets: shaded mass, lit north-west plane, contact
          // shadow to the south-east. The same three planes as a boulder, a
          // roof and a tower — one light, one language.
          g.ellipse(x + 1, y + r * 0.55, r * 1.05, r * 0.42).fill({ color: ink(pal.ink), alpha: 0.4 });
          g.poly([x - r, y + r * 0.5, x - r * 0.5, y - r, x + r * 0.6, y - r * 0.4, x + r, y + r * 0.55])
            .fill({ color: mark(shade(pal.base, 1.5)), alpha: 0.85 });
          g.poly([x - r, y + r * 0.5, x - r * 0.5, y - r, x - r * 0.05, y + r * 0.1]).fill({
            color: mark(shade(pal.alt, 1.25)),
            alpha: 0.8,
          });
          for (let j = 0; j < 2; j++) {
            const a = hash2(i * 3 + j, variant, 63) * Math.PI * 2;
            g.moveTo(x, y + r * 0.5)
              .lineTo(x + Math.cos(a) * r * 2.1, y + r * 0.5 + Math.sin(a) * r * 0.9)
              .stroke({ width: 1.1, color: ink(pal.ink), alpha: 0.55 });
          }
        }
        break;
      }
      case Terrain.StoneOutcrop: {
        // Ground is fractured shale and tiny chips. The harvestable stones
        // are the independently drawn nodes, so an empty tile is visibly empty.
        for (let i = 0; i < 2; i++) {
          const x = 5 + hash2(i, variant, 13) * (S - 12);
          const y = 5 + hash2(i, variant, 77) * (S - 12);
          const r = 1.1 + hash2(i, variant, 41) * 1.3;
          g.poly([x - r, y, x, y - r * 0.45, x + r * 1.5, y + r * 0.2, x, y + r * 0.6])
            .fill({ color: mark(mix(pal.alt, 0x939d9a, 0.42)), alpha: 0.65 });
          g.moveTo(x + 3, y - 3).lineTo(x + 7, y - 1).lineTo(x + 9, y - 2)
            .stroke({ width: 0.65, color: ink(pal.ink), alpha: 0.35 });
        }
        break;
      }
      case Terrain.IronSeam: {
        // Low, oxidised flakes sit flush with the ground under the dark ore nodes.
        for (let i = 0; i < 2; i++) {
          const x = 5 + hash2(i, variant, 23) * (S - 12);
          const y = 5 + hash2(i, variant, 87) * (S - 12);
          const r = 1.2 + hash2(i, variant, 5) * 1.2;
          g.poly([x - r, y, x + r, y - r * 0.4, x + r * 2, y + r * 0.5, x, y + r])
            .fill({ color: mark(0x79553e), alpha: 0.55 });
          g.moveTo(x - r, y).lineTo(x + r, y - r * 0.4)
            .stroke({ width: 0.7, color: mark(0x798c96), alpha: 0.6 });
        }
        break;
      }
      case Terrain.Marsh: {
        // Standing water in the hollows: a dark pool with a cold sky-glint on
        // its north-west lip, and reeds standing out of it.
        for (let i = 0; i < 3; i++) {
          const x = 6 + hash2(i, variant, 33) * (S - 12);
          const y = 6 + hash2(i, variant, 67) * (S - 12);
          g.ellipse(x, y, 5.5, 3).fill({ color: ink(shade(pal.ink, 0.8)), alpha: 0.7 });
          g.ellipse(x - 1, y - 0.8, 3, 1.4).fill({ color: mark(shade(pal.alt, 1.5)), alpha: 0.4 });
          for (let j = 0; j < 3; j++) {
            const rx = x - 4 + hash2(i * 4 + j, variant, 69) * 8;
            g.moveTo(rx, y + 2)
              .lineTo(rx + 0.8, y - 4 - hash2(i * 4 + j, variant, 70) * 3)
              .stroke({ width: 0.9, color: mark(shade(pal.alt, 1.2)), alpha: 0.55 });
          }
        }
        break;
      }
      case Terrain.River: {
        /**
         * A ford, and the read wanted is *you can see the bottom*.
         *
         * The opaque wash that used to be here has gone: the channel's colour
         * and the shape of its bed now come from the chunk's own ground, where
         * they can shade into the bank instead of stopping at a tile edge. What
         * is left is the surface — the standing waves a shallow makes over
         * stones, and the stones showing through them.
         */
        for (let i = 0; i < 3; i++) {
          const x = hash2(i, variant, 141) * S;
          const y = hash2(i, variant, 143) * S;
          g.ellipse(x, y, 2.2, 1.2).fill({ color: ink(pal.ink), alpha: 0.5 });
        }
        /**
         * **Wavelets, not lines**, and this is a fault the first cut shipped.
         *
         * A stroke that runs edge to edge in a 32-pixel tile is a stroke that
         * runs edge to edge across the *map*: there are four variants and a
         * river is a hundred tiles long, so four horizontal lines per tile
         * joined up into a barcode of continuous blue rules from bank to bank.
         * A standing wave is a short thing over a stone. Placed by hash and
         * kept clear of the edges, so nothing can join.
         */
        for (let i = 0; i < 5; i++) {
          const x = 3 + hash2(i, variant, 145) * (S - 12);
          const y = 3 + hash2(i, variant, 147) * (S - 6);
          const w = 4 + hash2(i, variant, 149) * 5;
          g.moveTo(x, y)
            .quadraticCurveTo(x + w * 0.5, y - 2.2, x + w, y)
            .stroke({ width: 1.1, color: mark(0x6f8e9c), alpha: 0.34 });
        }
        break;
      }
      /**
       * Deep water: the same current, drawn slower and colder.
       *
       * Fewer lines, wider apart, lower contrast — a surface with nothing under
       * it you can see. The read wanted at a glance is *depth*, and the cheapest
       * honest cue for that is the bed disappearing: no stones, no standing
       * waves, and a single long swell that catches the sky.
       */
      case Terrain.Deep: {
        // Two long swells rather than the ford's five short ones, and kept off
        // the tile edges for the reason written up there.
        for (let i = 0; i < 2; i++) {
          const x = 2 + hash2(i, variant, 151) * (S - 18);
          const y = 4 + hash2(i, variant, 153) * (S - 8);
          const w = 11 + hash2(i, variant, 157) * 5;
          g.moveTo(x, y)
            .quadraticCurveTo(x + w * 0.5, y - 1.6, x + w, y)
            .stroke({ width: 1.4, color: mark(0x4d6e7c), alpha: 0.18 });
        }
        break;
      }
      /**
       * A bridge, drawn as a structure and not as a floor.
       *
       * Two things were wrong with the planks that shipped. They always ran the
       * same way, so a bridge crossing an east–west road was a deck laid along
       * it rather than across it; and they had no thickness, so the one built
       * object on the water was the only thing in the game with no shadow under
       * it. Both are fixed here: `variant & 2` carries the run of the deck —
       * `scene.ts` reads the neighbours and picks — and the deck is drawn the
       * way `curtainart.ts` draws a wall, as a lit top over a shaded edge with
       * the water darkened beneath it.
       */
      case Terrain.Bridge: {
        const alongY = (variant & 2) !== 0;
        const deck = mark(pal.base);
        const lit = mark(shade(pal.alt, 1.5));
        const edge = ink(shade(pal.ink, 0.75));
        // The water in the shadow of the span.
        g.rect(0, 0, S, S).fill({ color: 0x000000, alpha: 0.22 });
        for (let i = 0; i < 5; i++) {
          const t = (i / 5) * S + 1;
          const wide = S / 5 - 1.5;
          if (alongY) {
            g.rect(0, t, S, wide).fill({ color: deck, alpha: 0.96 });
            g.rect(0, t, S, 1.3).fill({ color: lit, alpha: 0.85 });
            g.rect(0, t + wide - 1, S, 1).fill({ color: edge, alpha: 0.6 });
          } else {
            g.rect(t, 0, wide, S).fill({ color: deck, alpha: 0.96 });
            g.rect(t, 0, 1.3, S).fill({ color: lit, alpha: 0.85 });
            g.rect(t + wide - 1, 0, 1, S).fill({ color: edge, alpha: 0.6 });
          }
        }
        // Kerbs down the exposed sides, with the north-west one catching the
        // light — this is the whole of what makes it a bridge and not a raft.
        if (alongY) {
          g.rect(0, 0, S, 2.4).fill({ color: lit, alpha: 0.9 });
          g.rect(0, S - 2.6, S, 2.6).fill({ color: edge, alpha: 0.9 });
        } else {
          g.rect(0, 0, 2.4, S).fill({ color: lit, alpha: 0.9 });
          g.rect(S - 2.6, 0, 2.6, S).fill({ color: edge, alpha: 0.9 });
        }
        break;
      }
      case Terrain.Ruin: {
        // Old footings: a block, its lit top, and the shadow it throws to the
        // south-east. Three planes, the same three as everything else.
        for (let i = 0; i < 2; i++) {
          const x = 6 + hash2(i, variant, 19) * (S - 14);
          const y = 6 + hash2(i, variant, 71) * (S - 14);
          const w = 5 + hash2(i, variant, 3) * 7;
          const h = 4 + hash2(i, variant, 9) * 5;
          g.rect(x + 1.5, y + 1.5, w, h).fill({ color: ink(pal.ink), alpha: 0.45 });
          g.rect(x, y, w, h).fill(mark(shade(pal.alt, 1.35)));
          g.rect(x, y, w, 1.6).fill(mark(shade(pal.alt, 1.75)));
          g.rect(x, y + h - 1.4, w, 1.4).fill({ color: ink(pal.ink), alpha: 0.6 });
        }
        break;
      }
      case Terrain.Road: {
        /**
         * A worked track: two ruts running the length of it, and the stones
         * thrown out of them. The ruts are what makes it read as travelled.
         *
         * **Along the road, which is not always east–west.** These ran flat in
         * every tile, so a north–south track came out rung like a ladder — the
         * same fault the bridge had, and it takes the same fix: `variant & 2`
         * carries the run of the road, read off the neighbours in
         * `Scene.tileVariant`. A rut *should* meet the rut in the next tile, so
         * unlike the drift and the dune these stay full width; what was wrong
         * was the axis, not the length.
         */
        const alongY = (variant & 2) !== 0;
        for (let i = 0; i < 2; i++) {
          const off = S * (0.34 + i * 0.3) + (hash2(i, variant, 111) - 0.5) * 3;
          if (alongY) {
            g.moveTo(off, 0)
              .bezierCurveTo(off - 2, S * 0.35, off + 2, S * 0.7, off, S)
              .stroke({ width: 2.4, color: ink(pal.ink), alpha: 0.32 });
          } else {
            g.moveTo(0, off)
              .bezierCurveTo(S * 0.35, off - 2, S * 0.7, off + 2, S, off)
              .stroke({ width: 2.4, color: ink(pal.ink), alpha: 0.32 });
          }
        }
        for (let i = 0; i < 4; i++) {
          const x = hash2(i, variant, 101) * S;
          const y = hash2(i, variant, 103) * S;
          g.circle(x, y, 0.5 + hash2(i, variant, 107) * 0.8).fill({
            color: mark(shade(pal.alt, 1.08)),
            alpha: 0.25,
          });
        }
        break;
      }
      case Terrain.Ditch: {
        // A hole, read as a hole: a lit lip along the top edge, deep shadow
        // pooling toward the bottom, and spoil heaped on the near side. The
        // ground beneath already digs the cut — this is the lip and the stakes.
        g.rect(0, 0, S, S * 0.2).fill({ color: mark(shade(pal.alt, 1.4)), alpha: 0.55 });
        g.rect(0, S * 0.22, S, S * 0.46).fill({ color: 0x0d0c0a, alpha: 0.5 });
        for (let i = 0; i < 5; i++) {
          const x = hash2(i, variant, 211) * S;
          const y = S * 0.74 + hash2(i, variant, 223) * S * 0.2;
          g.circle(x, y, 1.4 + hash2(i, variant, 227) * 2).fill({
            color: mark(shade(pal.alt, 1.1)),
            alpha: 0.6,
          });
        }
        // Stakes at the bottom, for the ones who climb down anyway.
        for (let i = 0; i < 3; i++) {
          const x = 5 + hash2(i, variant, 233) * (S - 10);
          g.moveTo(x, S * 0.66)
            .lineTo(x + 1.5, S * 0.34)
            .stroke({ width: 1.4, color: mark(0x4a4136), alpha: 0.75 });
        }
        break;
      }
      case Terrain.Leyline: {
        // A seam of cold light under the ash, not on it: a thin vein wandering
        // across the tile, and a handful of motes above it. Drawn faint on
        // purpose — the ground stays under the units, and a player who is
        // looking for a leyline will find a patch of them, never one tile.
        const y0 = S * (0.25 + hash2(0, variant, 311) * 0.5);
        g.moveTo(0, y0)
          .bezierCurveTo(S * 0.35, y0 - S * 0.2, S * 0.65, y0 + S * 0.22, S, y0)
          .stroke({ width: 1.3, color: 0x6f8ec4, alpha: 0.45 });
        g.moveTo(0, y0)
          .bezierCurveTo(S * 0.35, y0 - S * 0.2, S * 0.65, y0 + S * 0.22, S, y0)
          .stroke({ width: 3.4, color: 0x3d5c8a, alpha: 0.16 });
        for (let i = 0; i < 4; i++) {
          const x = hash2(i, variant, 317) * S;
          const y = hash2(i, variant, 331) * S;
          g.circle(x, y, 0.7 + hash2(i, variant, 337) * 1.1).fill({ color: 0x9fb6e0, alpha: 0.35 });
        }
        break;
      }
      /**
       * Open ground, and this is where a country is legible at close zoom.
       *
       * The chunk's ground layer carries the colour, the shape and the coarse
       * grain of every biome, but it is baked at four pixels to a tile — wind
       * the camera in to seventy-four pixels a tile and it is a soft wash. The
       * crisp mark that says *snow*, *sand*, *scree* or *fen* has to be drawn
       * at this pitch, and open ground is the only surface a country wholly
       * owns: a forest floor is a forest floor in any weather, but bare ground
       * is precisely what a country *is*.
       *
       * One mark each, all of them lit from the north-west, none of them a
       * symbol. This is the settlement's half of "every biome on one
       * vocabulary".
       */
      default: {
        switch (this.theme.open) {
          case 'drift': {
            /**
             * Snow: sastrugi — short wind-packed crests with a lit north-west
             * face, and the scoured grey of what is under them in the lee.
             *
             * **Short, and this is the fault the first cut shipped.** These
             * crests ran from `-2` to `S + 2`, which is edge to edge of a
             * thirty-two pixel tile — and there are four variants against a
             * snowfield forty thousand tiles wide, so they joined end to end
             * into unbroken horizontal rules from one side of the map to the
             * other. `cityart-01-hold-ice-z30` is a barcode. It is exactly the
             * fault the river's wavelets were fixed for two cases above, in a
             * country where it covers everything rather than a channel.
             *
             * The long drift *shape* is not lost by making these short: it lives
             * in the ground layer's `drift` fabric, which is sampled in world
             * space and knows nothing about tile boundaries. That is the whole
             * reason the two layers are two.
             */
            for (let i = 0; i < 4; i++) {
              const x = 2 + hash2(i, variant, 401) * (S - 16);
              const y = 3 + hash2(i, variant, 402) * (S - 6);
              const w = 8 + hash2(i, variant, 403) * 6;
              const rise = 1.6 + hash2(i, variant, 404) * 2.2;
              g.moveTo(x, y)
                .quadraticCurveTo(x + w * 0.5, y + rise, x + w, y)
                .stroke({ width: 3, color: mark(0xb9c6d4), alpha: 0.26 });
              g.moveTo(x, y - 1.3)
                .quadraticCurveTo(x + w * 0.5, y + rise - 1.3, x + w, y - 1.3)
                .stroke({ width: 1.2, color: mark(0xdae6f2), alpha: 0.4 });
            }
            for (let i = 0; i < 5; i++) {
              const x = hash2(i, variant, 407) * S;
              const y = hash2(i, variant, 409) * S;
              g.circle(x, y, 0.6 + hash2(i, variant, 411) * 1.1).fill({
                color: mark(0xe4eef8),
                alpha: 0.3,
              });
            }
            break;
          }
          case 'dune': {
            /**
             * Sand: a slip face. The crest catches the light and the lee falls
             * away into shadow, which is the only cue a dune has.
             *
             * Bounded inside the tile for the reason written at `drift` — these
             * ran edge to edge too, and four variants of a full-width curve
             * repeated across a sand sea is corduroy. The long warped crests
             * are the ground layer's `dune` fabric; what belongs at this pitch
             * is the *lip* of one, and the ripple beside it.
             */
            for (let i = 0; i < 3; i++) {
              const x = 1 + hash2(i, variant, 421) * (S - 18);
              const y = 5 + hash2(i, variant, 422) * (S - 10);
              const w = 10 + hash2(i, variant, 423) * 7;
              g.moveTo(x, y + 2.5)
                .quadraticCurveTo(x + w * 0.5, y - 3, x + w, y + 1)
                .stroke({ width: 4, color: ink(pal.ink), alpha: 0.26 });
              g.moveTo(x, y + 0.8)
                .quadraticCurveTo(x + w * 0.5, y - 4.6, x + w, y - 0.6)
                .stroke({ width: 1.5, color: mark(shade(pal.alt, 1.5)), alpha: 0.46 });
            }
            for (let i = 0; i < 5; i++) {
              const x = hash2(i, variant, 425) * (S - 9);
              const y = hash2(i, variant, 427) * S;
              g.moveTo(x, y)
                .lineTo(x + 6 + hash2(i, variant, 429) * 3, y - 1.4)
                .stroke({ width: 0.8, color: mark(shade(pal.alt, 1.25)), alpha: 0.22 });
            }
            break;
          }
          case 'scree': {
            // High stone: chips and shatter, small and many. The country the
            // relief layer exists for still needs something under a boot.
            for (let i = 0; i < 7; i++) {
              const x = hash2(i, variant, 431) * S;
              const y = hash2(i, variant, 433) * S;
              const r = 1.2 + hash2(i, variant, 437) * 2.2;
              g.poly([x - r, y + r * 0.5, x - r * 0.3, y - r, x + r, y + r * 0.4]).fill({
                color: mark(shade(pal.alt, 1.6)),
                alpha: 0.7,
              });
              g.poly([x - r, y + r * 0.5, x - r * 0.3, y - r, x - r * 0.15, y + r * 0.1]).fill({
                color: mark(shade(pal.alt, 2.0)),
                alpha: 0.6,
              });
            }
            break;
          }
          case 'litter': {
            // A wooded country's open ground is still a clearing in a wood:
            // needle, leaf and the odd fallen branch.
            for (let i = 0; i < 9; i++) {
              const x = hash2(i, variant, 441) * S;
              const y = hash2(i, variant, 443) * S;
              const a = hash2(i, variant, 447) * Math.PI;
              const l = 1.6 + hash2(i, variant, 449) * 2.6;
              g.moveTo(x - Math.cos(a) * l, y - Math.sin(a) * l)
                .lineTo(x + Math.cos(a) * l, y + Math.sin(a) * l)
                .stroke({ width: 1.1, color: mark(shade(pal.alt, 1.3)), alpha: 0.45 });
            }
            break;
          }
          case 'reed': {
            // Fen: tussocks standing out of wet ground, and the wet showing
            // between them.
            for (let i = 0; i < 4; i++) {
              const x = 3 + hash2(i, variant, 451) * (S - 6);
              const y = 4 + hash2(i, variant, 453) * (S - 8);
              g.ellipse(x, y + 1.4, 3.4, 1.3).fill({ color: ink(pal.ink), alpha: 0.4 });
              for (let j = 0; j < 4; j++) {
                const dx = (hash2(i * 5 + j, variant, 457) - 0.5) * 5;
                g.moveTo(x + dx * 0.4, y + 1)
                  .lineTo(x + dx, y - 3.4 - hash2(i * 5 + j, variant, 459) * 2.6)
                  .stroke({ width: 0.9, color: mark(shade(pal.alt, 1.3)), alpha: 0.6 });
              }
            }
            break;
          }
          case 'shingle': {
            // A shore: a fine ripple and shell grit, the smoothest ground on
            // any map and drawn as such.
            // Short, not full width: see `drift`. A beach ripple that spans the
            // tile spans the shore.
            for (let i = 0; i < 5; i++) {
              const x = 1 + hash2(i, variant, 460) * (S - 14);
              const y = hash2(i, variant, 461) * S;
              const w = 8 + hash2(i, variant, 462) * 5;
              g.moveTo(x, y)
                .quadraticCurveTo(x + w * 0.5, y + 1.4, x + w, y)
                .stroke({ width: 1, color: mark(shade(pal.alt, 1.35)), alpha: 0.3 });
            }
            for (let i = 0; i < 6; i++) {
              const x = hash2(i, variant, 463) * S;
              const y = hash2(i, variant, 467) * S;
              g.circle(x, y, 0.6 + hash2(i, variant, 469) * 0.9).fill({
                color: mark(shade(pal.alt, 1.7)),
                alpha: 0.4,
              });
            }
            break;
          }
          case 'rubble': {
            // Somebody was here before: a course of stone under the ash, at the
            // angle their street ran and not at the angle of the tile grid.
            for (let i = 0; i < 3; i++) {
              const x = hash2(i, variant, 471) * S;
              const y = hash2(i, variant, 473) * S;
              const w = 3 + hash2(i, variant, 477) * 6;
              g.rect(x + 1, y + 1, w, 2.6).fill({ color: ink(pal.ink), alpha: 0.4 });
              g.rect(x, y, w, 2.6).fill({ color: mark(shade(pal.alt, 1.4)), alpha: 0.65 });
              g.rect(x, y, w, 0.9).fill({ color: mark(shade(pal.alt, 1.8)), alpha: 0.55 });
            }
            break;
          }
          default: {
            // Broad grass colour belongs to the terrain below. Sparse, low
            // contrast tussocks leave room for paths, shadows and people.
            for (let i = 0; i < 3; i++) {
              const x = 2 + hash2(i, variant, 481) * (S - 4);
              const y = 3 + hash2(i, variant, 483) * (S - 5);
              g.ellipse(x, y + 1, 2, 0.6).fill({ color: ink(pal.ink), alpha: 0.12 });
              for (let j = 0; j < 2; j++) {
                const dx = (hash2(i * 4 + j, variant, 487) - 0.5) * 3.6;
                g.moveTo(x, y + 1)
                  .lineTo(x + dx, y - 0.8 - hash2(i * 4 + j, variant, 491) * 1.4)
                  .stroke({ width: 0.7, color: mark(shade(pal.alt, 1.2)), alpha: 0.26 });
              }
            }
            // The stones a plough throws out, lit north-west like everything
            // else on this ground.
            for (let i = 0; i < 1; i++) {
              const x = hash2(i, variant, 493) * S;
              const y = hash2(i, variant, 495) * S;
              const r = 0.55 + hash2(i, variant, 497) * 0.65;
              g.poly([x - r, y + r * 0.5, x - r * 0.3, y - r, x + r, y + r * 0.4]).fill({
                color: mark(shade(pal.alt, 1.22)),
                alpha: 0.24,
              });
            }
            break;
          }
        }
        break;
      }
    }

    // No edge darkening: at close zoom it turned the ground into visible graph
    // paper. Variation has to come from the grain, not from the tile boundary.
    return this.bake(g, S, S);
  }

  // ── Walls ────────────────────────────────────────────────────────────────

  private buildWalls(): void {
    const S = ART_TILE_PX;
    // Bit-mask of connected neighbours: 1=N 2=E 4=S 8=W. Bake all 16.
    for (let mask = 0; mask < 16; mask++) {
      const g = new Graphics();
      const stone = 0x565045;
      const top = 0x6b6558;
      const dark = 0x322d26;

      const core = S * 0.56;
      const half = core / 2;
      const cx = S / 2;
      const cy = S / 2;

      const arm = (dx: number, dy: number) => {
        const x = cx + (dx * S) / 2 - (dx === 0 ? half : dx > 0 ? 0 : (S / 2));
        const y = cy + (dy * S) / 2 - (dy === 0 ? half : dy > 0 ? 0 : (S / 2));
        const w = dx === 0 ? core : S / 2;
        const h = dy === 0 ? core : S / 2;
        g.rect(x, y, w, h).fill(stone);
      };

      g.rect(cx - half, cy - half, core, core).fill(stone);
      if (mask & 1) arm(0, -1);
      if (mask & 2) arm(1, 0);
      if (mask & 4) arm(0, 1);
      if (mask & 8) arm(-1, 0);

      // Crenellations and a lit top edge, so walls read as solid volume.
      g.rect(cx - half, cy - half, core, 3).fill(top);
      g.rect(cx - half, cy + half - 2.5, core, 2.5).fill(dark);
      for (let i = 0; i < 3; i++) {
        g.rect(cx - half + 2 + i * (core / 3), cy - half - 1.5, core / 6, 3).fill(top);
      }
      this.walls[`w${mask}`] = this.bake(g, S, S);
    }

    // Gate: timber leaves with iron bands.
    for (const vertical of [false, true]) {
      const g = new Graphics();
      const w = vertical ? S * 0.6 : S;
      const h = vertical ? S : S * 0.6;
      const x = (S - w) / 2;
      const y = (S - h) / 2;
      g.rect(x, y, w, h).fill(0x4a3a2a);
      for (let i = 0; i < 4; i++) {
        if (vertical) g.rect(x, y + (i * h) / 4, w, 1.6).fill(0x30271d);
        else g.rect(x + (i * w) / 4, y, 1.6, h).fill(0x30271d);
      }
      g.rect(x, y, w, 2.4).fill(0x6b5540);
      g.rect(x, y, w, h).stroke({ width: 2, color: 0x2a2621 });
      g.circle(S / 2, S / 2, 3).fill(0x8a8378);
      this.walls[vertical ? 'gateV' : 'gateH'] = this.bake(g, S, S);
    }

    // Rubble left where a wall used to be.
    const rub = new Graphics();
    for (let i = 0; i < 7; i++) {
      const x = 4 + hash2(i, 3, 13) * (S - 8);
      const y = 4 + hash2(i, 3, 29) * (S - 8);
      const r = 1.6 + hash2(i, 3, 47) * 3;
      rub.poly([x - r, y + r * 0.6, x - r * 0.3, y - r, x + r, y + r * 0.4]).fill(0x4a453d);
    }
    this.walls.rubble = this.bake(rub, S, S);
  }

  // ── Units ────────────────────────────────────────────────────────────────

  private buildUnits(): void {
    for (let i = 0; i < UNIT_TYPES.length; i++) {
      const t = UNIT_TYPES[i];
      const fac = t.faction === 'common' ? undefined : faction(t.faction);
      const accent = fac?.accent ?? C.gold;
      // Everything is lifted well above the ground values: the world is ash, and
      // the only way a soldier reads against ash is to be brighter than it.
      const cloth = shade(fac?.cloth ?? 0x45403a, 1.35);
      const metal = shade(fac?.metal ?? 0x8a8378, 1.12);
      this.units[i] = [
        this.bakeUnit(t, accent, cloth, metal, C.friendlyRim),
        // Invaders: the same build, lit cold. Hue carries the side, value stays
        // high enough that they are just as legible in a night fight.
        this.bakeUnit(
          t,
          mix(accent, 0x8fa6d8, 0.4),
          mix(cloth, 0x54637f, 0.5),
          mix(metal, 0x93a4bb, 0.45),
          C.hostileRim,
        ),
      ];
    }
  }

  private bakeUnit(t: UnitType, accent: number, cloth: number, metal: number, rim: number): UnitArt {
    const S = ART_UNIT_PX;
    const vis = t.vis;
    const p: Palette = { accent, cloth, metal, rim };

    const frameCount = vis.build === 'siege' ? 1 : UNIT_WALK_FRAMES;
    const bakeFacing = (direction: number, pose: UnitPose): Texture[] => {
      const frames: Texture[] = [];
      const count = pose === 'idle' ? 1 : pose === 'work' ? UNIT_WORK_FRAMES : frameCount;
      for (let f = 0; f < count; f++) {
        const body = new Graphics();
        drawLowPolyUnit(body, vis, p, f, direction, pose);
        const holder = new Container();
        holder.addChild(body);
        holder.setFromMatrix(new Matrix(1, 0, 0, 1, S / 2, S * 0.74));
        frames.push(this.bake(holder, S, S, 2));
        holder.destroy({ children: true });
      }
      if (frames.length === 1 && pose !== 'idle') while (frames.length < UNIT_WALK_FRAMES) frames.push(frames[0]);
      return frames;
    };
    // Each direction and action is independently lazy. Standing villagers do
    // not allocate 96 walk/work textures merely because their type is visible.
    const lazyDirections = <T>(make: (direction: number) => T): T[] => {
      const directions: T[] = new Array(8);
      for (let direction = 0; direction < 8; direction++) {
        Object.defineProperty(directions, direction, { configurable: true, get() {
          const value = make(direction);
          Object.defineProperty(directions, direction, { configurable: true, value });
          return value;
        } });
      }
      return directions;
    };
    const idle = lazyDirections(direction => bakeFacing(direction, 'idle')[0]);
    const facings = lazyDirections(direction => vis.build === 'siege'
      ? Array.from({ length: UNIT_WALK_FRAMES }, () => idle[direction]) : bakeFacing(direction, 'walk'));
    const work = vis.build === 'villager' ? lazyDirections(direction => bakeFacing(direction, 'work')) : undefined;

    /**
     * The weapon box is sized to the weapon rather than fixed.
     *
     * `scene` turns the pivot into a sprite anchor by dividing by 48, so what it
     * actually consumes is the pivot's *fraction* of the texture — which means
     * the box may be any size as long as the pivot is reported against 48. That
     * matters: under the old fixed 48-pixel box a warlord's axe at scale 1.6 was
     * sawn off at the haft, and a pike could never be longer than a sword.
     */
    const b = WEAPON_BOUNDS[vis.weapon];
    const sd = drawScale(vis.scale);
    const m = 4;
    const px = b ? -b.x0 * sd + m : 10;
    const ws = b
      ? Math.ceil(Math.max(48, px + b.x1 * sd + m, 2 * (Math.max(-b.y0, b.y1) * sd + m)))
      : 48;

    const weaponG = new Graphics();
    drawWeapon(weaponG, vis.weapon, p, sd);
    const wHolder = new Container();
    wHolder.addChild(weaponG);
    wHolder.setFromMatrix(new Matrix(1, 0, 0, 1, px, ws / 2));
    const weapon = this.bake(wHolder, ws, ws, 2);
    wHolder.destroy({ children: true });

    return {
      get body() { return idle[0]; },
      get frames() { return facings[0]; },
      directionFrames: facings,
      directionIdle: idle,
      directionWorkFrames: work,
      directionHands: Array.from({ length: 8 }, (_, direction) => lowPolyHand(vis, direction)),
      directionFrameHands: Array.from({ length: 8 }, (_, direction) =>
        Array.from({ length: UNIT_WALK_FRAMES }, (_, frame) => lowPolyHand(vis, direction, frame, 'walk'))),
      directionWorkHands: work ? Array.from({ length: 8 }, (_, direction) =>
        Array.from({ length: UNIT_WORK_FRAMES }, (_, frame) => lowPolyHand(vis, direction, frame, 'work'))) : undefined,
      bodyAnchorY: 0.74,
      weapon,
      weaponPivotX: (px / ws) * 48,
      weaponPivotY: 24,
      scale: vis.scale,
    };
  }

  // ── Buildings ────────────────────────────────────────────────────────────

  private bakeBuilding(def: BuildingType, skin: Vernacular): { texture: Texture; geometry: BuildingGeometry } {
    const px = ART_TILE_PX;
    const W = def.w * px;
    const H = def.h * px;
    // Headroom above the ground footprint for roofs, spires and windmill sails.
    const pad = Math.round(px * 1.4 * def.vis.height);
    const g = new Graphics();
    const projected = drawLowPolyBuilding(g, def, skin, W, H, pad);
    // Extreme eaves and roof motifs may exceed a narrow footprint. Fit the
    // complete drawing, preserving aspect and the ground line, before baking.
    const bounds = g.getLocalBounds();
    const fit = Math.min(1, (W - 2) / bounds.width, (H + pad - 2) / bounds.height);
    g.scale.set(fit);
    g.x = Math.max(1 - bounds.minX * fit, Math.min(0, W - 1 - bounds.maxX * fit));
    g.y = Math.max(1 - pad - bounds.minY * fit, Math.min(0, H - 1 - bounds.maxY * fit));
    const holder = new Container();
    holder.addChild(g);
    holder.setFromMatrix(new Matrix(1, 0, 0, 1, 0, pad));
    const texture = this.bake(holder, W, H + pad, 1.5);
    const silhouette = projected.silhouette.map(([x, y]): [number, number] =>
      [x * fit + g.x, y * fit + g.y + pad]);
    const geometry: BuildingGeometry = {
      silhouette,
      groundCenterX: projected.groundCenterX * fit + g.x,
      groundFootprint: projected.groundFootprint.map(([x, y]): [number, number] =>
        [x * fit + g.x, y * fit + g.y + pad]),
      groundFrontY: projected.groundFrontY * fit + g.y + pad,
      bounds: {
        x0: Math.min(...silhouette.map(p => p[0])), y0: Math.min(...silhouette.map(p => p[1])),
        x1: Math.max(...silhouette.map(p => p[0])), y1: Math.max(...silhouette.map(p => p[1])),
      },
    };
    holder.destroy({ children: true });
    return { texture, geometry };
  }
}

/** Which war-map token stands in for this unit when the camera pulls back. */
export function tokenIndexFor(flags: number, build: string): number {
  // A boss is a star whatever body it happens to wear. The three Siege bosses
  // are brutes and casters by build, and reading them as "another big one" is
  // exactly the mistake that gets a line eaten.
  if ((flags & UF.Champion) !== 0 || build === 'champion') return 7;
  if (build === 'villager') return 6;
  if (build === 'siege') return 3;
  if (build === 'brute') return 5;
  if (build === 'caster') return 4;
  if (build === 'cavalry') return 2;
  if (build === 'archer') return 1;
  if (build === 'swarm') return 8;
  if (build === 'flier') return 9;
  return 0;
}

// ── Unit silhouettes ───────────────────────────────────────────────────────

/** Near-black. Every silhouette is separated from the ground by a ring of it. */
const INK = 0x0d0b09;

interface Palette {
  accent: number;
  cloth: number;
  metal: number;
  rim: number;
}

/** A path issued onto a Graphics, left unfilled so it can be drawn more than once. */
type Path = (g: Graphics) => void;

/** Ink outline only — for parts that sit inside the outer silhouette. */
function inked(g: Graphics, path: Path, fill: number, ink = 3.0, alpha = 0.9): void {
  path(g);
  g.fill({ color: INK, alpha }).stroke({ width: ink, color: INK, alpha, join: 'round' });
  path(g);
  g.fill(fill);
}

/** Stroke a path twice: a fat ink pass, then the bright pass over it. */
function inkedStroke(g: Graphics, path: Path, width: number, color: number, alpha = 1): void {
  path(g);
  g.stroke({ width: width + 2.8, color: INK, alpha: 0.92, cap: 'round', join: 'round' });
  path(g);
  g.stroke({ width, color, alpha, cap: 'round', join: 'round' });
}

/**
 * Drawing scale, compressed above 1.15.
 *
 * The art box is fixed and `vis.scale` runs to 1.7, which is more than a
 * champion with a cape, a crown of blades and a standard on its back can fit
 * inside. Compressing the top of the range keeps the big models unmistakably
 * bigger without sawing anything off — and nobody on a battlefield can measure
 * the difference between "1.7 times a man" and "1.45 times a man", whereas a
 * clipped banner is something everybody can see.
 *
 * `UnitArt.scale` still reports the *authored* scale, because the shadow and
 * the weapon-hand offset in `scene` are in world units and should not be lied
 * to about how big the model is meant to be.
 */
function drawScale(s: number): number {
  return s <= 1.15 ? s : 1.15 + (s - 1.15) * 0.55;
}

// ── Weapons ───────────────────────────────────────────────────────────────

/**
 * How far each weapon reaches from the hand, in authoring units, before scale.
 *
 * Used to cut a texture that actually fits the weapon — see `bakeUnit`. Keep
 * these honest: they are the bounding box, outline included, and a weapon that
 * draws outside its entry gets its tip shaved off.
 */
const WEAPON_BOUNDS: Partial<Record<VisWeapon, { x0: number; x1: number; y0: number; y1: number }>> = {
  sword: { x0: -13, x1: 39, y0: -9, y1: 9 },
  spear: { x0: -21, x1: 59, y0: -24, y1: 11 },
  axe: { x0: -14, x1: 38, y0: -20, y1: 11 },
  hammer: { x0: -13, x1: 41, y0: -14, y1: 14 },
  scythe: { x0: -16, x1: 46, y0: -33, y1: 9 },
  bow: { x0: -13, x1: 39, y0: -26, y1: 26 },
  crossbow: { x0: -17, x1: 31, y0: -22, y1: 22 },
  gun: { x0: -20, x1: 46, y0: -11, y1: 9 },
  staff: { x0: -21, x1: 47, y0: -13, y1: 13 },
  claw: { x0: -8, x1: 29, y0: -14, y1: 14 },
};

/**
 * Weapons are drawn along +x from a pivot at the hand.
 *
 * At this scale the weapon is not decoration, it is half the identification:
 * you know a pike block from a bow line by the hedge above it long before you
 * can see a face. So each one gets a real profile — the spear is long and
 * carried at an angle, the bow is drawn with a nocked arrow, the crossbow is a
 * blocky horizontal, the gun has a barrel and a lit match, the hammer is
 * top-heavy, the scythe hooks back on itself — and each gets its own ink
 * outline, because a blade drawn over a body of similar value simply vanishes.
 */
function drawWeapon(g: Graphics, weapon: VisWeapon, p: Palette, sIn: number): void {
  // Steel, not chrome. `p.metal` has already been lifted once for legibility;
  // lifting it again put every hammer head above the value of the man holding
  // it, and a weapon that out-values the soldier is a weapon you read first.
  const metal = shade(p.metal, 1.15);
  const metalLit = shade(p.metal, 1.42);
  const haft = 0x5c4a36;
  const haftDark = 0x3e3122;
  // Authored a size up, then taken back down: the profiles below are easier to
  // reason about at these numbers, and the head weights came out right at 0.88.
  const s = sIn * 0.88;

  switch (weapon) {
    case 'sword': {
      inked(g, (gg) => gg.rect(-8 * s, -1.8 * s, 10 * s, 3.6 * s), haft, 2.8);
      inked(g, (gg) => gg.poly([1.5 * s, -2.2 * s, 29 * s, -2.0 * s, 36 * s, 0, 29 * s, 2.0 * s, 1.5 * s, 2.2 * s]), metal, 3.2);
      g.rect(3 * s, -0.7 * s, 25 * s, 1.4 * s).fill(metalLit);
      inked(g, (gg) => gg.rect(-2.2 * s, -5.6 * s, 3.8 * s, 11.2 * s), p.accent, 2.8);
      g.circle(-8.4 * s, 0, 2.2 * s).fill(shade(p.accent, 0.7));
      break;
    }
    case 'spear': {
      // Carried across the body at a shallow angle rather than levelled: the
      // diagonal is what turns a rank of spearmen into a visible hedge.
      const a = -0.3;
      const cx = Math.cos(a);
      const cy = Math.sin(a);
      const at = (d: number, off: number): [number, number] => [
        (cx * d - cy * off) * s,
        (cy * d + cx * off) * s,
      ];
      const butt = at(-16, 0);
      const grip = at(46, 0);
      inkedStroke(g, (gg) => gg.moveTo(butt[0], butt[1]).lineTo(grip[0], grip[1]), 2.6 * s, haft);
      const t = at(56, 0);
      const l = at(45, -3.4);
      const r = at(45, 3.4);
      const w1 = at(48, -1.4);
      const w2 = at(48, 1.4);
      inked(g, (gg) => gg.poly([t[0], t[1], l[0], l[1], w1[0], w1[1], w2[0], w2[1], r[0], r[1]]), metal, 3.0);
      const cl = at(43, -3.0);
      const cr = at(43, 3.0);
      g.moveTo(cl[0], cl[1]).lineTo(cr[0], cr[1]).stroke({ width: 2.2 * s, color: p.accent });
      const b2 = at(-13, 0);
      g.circle(b2[0], b2[1], 2.0 * s).fill(haftDark);
      break;
    }
    case 'axe': {
      inkedStroke(g, (gg) => gg.moveTo(-9 * s, 0).lineTo(26 * s, 0), 3.0 * s, haft);
      // Bearded, and hanging entirely to one side: an unbalanced outline is the
      // fastest way to say "axe" without drawing an edge.
      inked(
        g,
        (gg) => gg.poly([14 * s, -2.6 * s, 22 * s, -16 * s, 33 * s, -12 * s, 35 * s, -1 * s, 27 * s, 4.6 * s, 15 * s, 3.0 * s]),
        metal,
        3.4,
      );
      g.poly([16 * s, -2.6 * s, 23 * s, -13 * s, 29 * s, -10.6 * s, 24 * s, -1.4 * s]).fill(metalLit);
      break;
    }
    case 'hammer': {
      inkedStroke(g, (gg) => gg.moveTo(-9 * s, 0).lineTo(20 * s, 0), 3.4 * s, haft);
      inked(g, (gg) => gg.rect(18 * s, -9 * s, 11 * s, 18 * s), metal, 3.6);
      g.rect(18 * s, -9 * s, 11 * s, 4.4 * s).fill(metalLit);
      // The beak on the far face, which is what makes it top-heavy and not a box.
      inked(g, (gg) => gg.poly([29 * s, -4.0 * s, 36 * s, 0, 29 * s, 4.0 * s]), shade(metal, 0.85), 2.8);
      g.rect(18 * s, -9 * s, 11 * s, 18 * s).stroke({ width: 1.3, color: p.accent, alpha: 0.7 });
      break;
    }
    case 'scythe': {
      inkedStroke(g, (gg) => gg.moveTo(-11 * s, 3.0 * s).lineTo(28 * s, -1.0 * s), 2.6 * s, haft);
      // The hook curls back over the wielder, which no other weapon here does.
      inkedStroke(
        g,
        (gg) =>
          gg
            .moveTo(27 * s, -1.5 * s)
            .bezierCurveTo(41 * s, -6 * s, 44 * s, -22 * s, 19 * s, -27 * s),
        3.4 * s,
        metal,
      );
      g.moveTo(27 * s, -3.4 * s)
        .bezierCurveTo(38 * s, -8 * s, 40 * s, -20 * s, 20 * s, -24.6 * s)
        .stroke({ width: 1.2 * s, color: metalLit, alpha: 0.9 });
      g.circle(27 * s, -1.5 * s, 2.2 * s).fill(p.accent);
      break;
    }
    case 'bow': {
      // Drawn, not held: limbs bent forward, string hauled back past the hand,
      // an arrow on it. A slack bow reads as a stick.
      inkedStroke(
        g,
        (gg) => gg.moveTo(0, -22 * s).bezierCurveTo(21 * s, -14 * s, 21 * s, 14 * s, 0, 22 * s),
        3.0 * s,
        0x6b5540,
      );
      g.moveTo(0, -22 * s)
        .lineTo(-8 * s, 0)
        .lineTo(0, 22 * s)
        .stroke({ width: 1.3 * s, color: C.boneDim, alpha: 0.9 });
      inkedStroke(g, (gg) => gg.moveTo(-8 * s, 0).lineTo(32 * s, 0), 1.5 * s, C.bone);
      g.poly([36 * s, 0, 29 * s, -2.8 * s, 29 * s, 2.8 * s]).fill(C.boneDim);
      g.rect(9 * s, -4.6 * s, 3.6 * s, 9.2 * s).fill(p.accent);
      break;
    }
    case 'crossbow': {
      // Blocky and horizontal: a stock along the arm and a short thick prod
      // across it. Reads at a glance as "not a bow", which is the entire job.
      inked(g, (gg) => gg.rect(-11 * s, -3.0 * s, 39 * s, 6.0 * s), 0x53422f, 3.2);
      inked(g, (gg) => gg.rect(-13 * s, -4.6 * s, 7 * s, 9.2 * s), haftDark, 2.8);
      inked(
        g,
        (gg) => gg.poly([13 * s, -19 * s, 19 * s, -17 * s, 19 * s, 17 * s, 13 * s, 19 * s, 15 * s, 0]),
        metal,
        3.4,
      );
      g.moveTo(15 * s, -18 * s)
        .lineTo(2 * s, 0)
        .lineTo(15 * s, 18 * s)
        .stroke({ width: 1.3 * s, color: C.boneDim, alpha: 0.85 });
      g.rect(2 * s, -1.3 * s, 24 * s, 2.6 * s).fill(C.bone);
      g.rect(-11 * s, -3.0 * s, 12 * s, 6.0 * s).fill({ color: p.accent, alpha: 0.5 });
      break;
    }
    case 'gun': {
      inked(
        g,
        (gg) => gg.poly([-16 * s, -3.0 * s, -5 * s, -4.6 * s, 3 * s, -3.4 * s, 3 * s, 3.4 * s, -5 * s, 5.0 * s, -16 * s, 4.0 * s]),
        0x3f342a,
        3.2,
      );
      // The barrel is the silhouette: long, thin, and it ends in a bell.
      inked(g, (gg) => gg.rect(2 * s, -3.0 * s, 36 * s, 6.0 * s), metal, 3.2);
      g.rect(2 * s, -3.0 * s, 36 * s, 2.0 * s).fill(metalLit);
      inked(g, (gg) => gg.poly([38 * s, -5.0 * s, 42 * s, -4.0 * s, 42 * s, 4.0 * s, 38 * s, 5.0 * s]), shade(metal, 0.8), 2.8);
      // The lit match, hooked over the lock. It is one dot and it sells the era.
      g.moveTo(-2 * s, -3.6 * s).lineTo(-4 * s, -7.0 * s).stroke({ width: 1.4 * s, color: 0x2e2620 });
      g.circle(-4.4 * s, -7.4 * s, 1.6 * s).fill({ color: C.emberLight, alpha: 0.95 });
      break;
    }
    case 'staff': {
      inkedStroke(g, (gg) => gg.moveTo(-16 * s, 2.4 * s).lineTo(29 * s, -1.2 * s), 2.8 * s, 0x4a3c2c);
      // Topped, and the topping is the point: a cage of horns round a light.
      for (const side of [-1, 1]) {
        inkedStroke(
          g,
          (gg) => gg.moveTo(26 * s, side * 1.5 * s).quadraticCurveTo(38 * s, side * 8 * s, 42 * s, side * 2 * s),
          2.0 * s,
          shade(p.metal, 1.1),
        );
      }
      g.circle(34 * s, 0, 10 * s).fill({ color: p.accent, alpha: 0.2 });
      inked(g, (gg) => gg.circle(34 * s, 0, 5.4 * s), p.accent, 3.0);
      g.circle(33 * s, -1 * s, 2.2 * s).fill(shade(p.accent, 1.5));
      break;
    }
    case 'claw': {
      inked(g, (gg) => gg.ellipse(0, 0, 4.6 * s, 5.6 * s), shade(p.cloth, 0.9), 2.8);
      for (let i = -1; i <= 1; i++) {
        inkedStroke(
          g,
          (gg) =>
            gg
              .moveTo(2 * s, i * 4.4 * s)
              .bezierCurveTo(12 * s, i * 6.4 * s, 19 * s, i * 4.6 * s, 24 * s, i * 1.6 * s),
          2.4 * s,
          C.bone,
        );
      }
      break;
    }
    case 'none':
    default:
      break;
  }
}
